import { FC, css } from "dreamland/core";
import type { ProjectCardDetails } from "../Projects";
import { getWebpPath } from "../lib/images";
import TransitionLink from "./TransitionLink";

function ProjectCard(
	this: FC<{
		project: ProjectCardDetails;
		eager?: boolean;
		// side-by-side thumbnail + text, for the homepage
		compact?: boolean;
	}>
) {
	const handleCardClick = (event: MouseEvent) => {
		if (
			event.defaultPrevented ||
			event.button !== 0 ||
			event.metaKey ||
			event.ctrlKey ||
			event.shiftKey ||
			event.altKey
		) {
			return;
		}

		const target = event.target as HTMLElement | null;
		if (target?.closest("a")) {
			return;
		}

		const card = event.currentTarget as HTMLElement | null;
		const link = card?.querySelector(
			"a[data-card-link]"
		) as HTMLAnchorElement | null;
		if (!link) return;

		const syntheticClick = new MouseEvent("click", {
			bubbles: true,
			cancelable: true,
			composed: true,
		});
		link.dispatchEvent(syntheticClick);
	};

	const thumbnail = this.project.getThumbnailPath();
	const thumbnailWebp = getWebpPath(thumbnail);
	const yearDisplay =
		typeof this.project.endYear === "number"
			? this.project.endYear === this.project.startYear
				? ` ${this.project.startYear}`
				: ` ${this.project.startYear} — ${this.project.endYear}`
			: ` ${this.project.startYear} — present`;

	return (
		<div
			class={`project-card-wrapper${this.compact ? " compact" : ""}`}
			on:click={handleCardClick}
		>
			<TransitionLink
				href={`/projects/${this.project.lastPathComponent}`}
				class="project-card"
				style="text-decoration: none; color: inherit;"
				data-card-link
			>
				<div class="image-wrapper">
					<picture>
						{thumbnailWebp ? (
							<source type="image/webp" srcset={thumbnailWebp} />
						) : (
							false
						)}
						<img
							src={thumbnail}
							alt=""
							loading={this.eager ? "eager" : "lazy"}
						/>
					</picture>
				</div>
				<div class="project-info">
					<p class="project-year">
						<span class="material-symbols">calendar_month</span>
						{yearDisplay}
						{this.project.featured ? (
							<span class="featured-indicator">
								•<span class="material-symbols">trophy</span> Featured
							</span>
						) : (
							false
						)}
					</p>
					<h3 class="project-title">{this.project.title}</h3>
					<p class="project-description">{this.project.blurb}</p>
				</div>
			</TransitionLink>
		</div>
	);
}

ProjectCard.style = css<typeof ProjectCard>`
	:scope {
		width: 100%;
		background: var(--card-bg);
		overflow: hidden;
		font-size: 0.9rem;
		text-decoration: none !important;
		transform: scale(1) translateY(0);
		transition:
			transform 0.2s,
			background-color 0.2s;
		z-index: 10;
	}

	:scope:hover {
		background-color: var(--card-bg-hover);
		transform: scale(1.01) translateY(-2px);
	}

	:scope:hover .project-title {
		text-decoration: underline !important;
	}

	/* Note: keyboard-focus styles for this card live in global style.css
	   (search for ".project-card-wrapper"). Dreamland's CSS scoping appends
	   :where(.dlcss-N) to every selector token, which would break selectors
	   like :has(a:focus-visible) here because the inner <a> doesn't carry
	   the component's scope class. */

	@media (orientation: portrait) {
		:scope {
			width: 100%;
		}
	}

	.project-info {
		padding: 1rem;
		padding-top: 0.5rem;
		display: flex;
		flex-direction: column;
		gap: 0.3rem;
	}

	.project-description,
	.project-year {
		color: var(--subtext1) !important;
	}

	.featured-indicator {
		display: inline-flex;
		align-items: center;
		gap: 0.25rem;
		padding-left: 0.25rem;
	}

	p {
		font-size: 0.8rem;
	}

	* {
		margin: 0 !important;
		text-decoration: none !important;
	}

	.image-wrapper {
		aspect-ratio: 16 / 9;
		overflow: hidden;
	}

	@supports (grid-template-rows: masonry) or (display: masonry) or
		(display: grid-lanes) {
		.image-wrapper {
			aspect-ratio: auto;
			overflow: visible;
		}
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-bottom: 1px solid var(--surface0);
	}

	:scope.compact > :global(.project-card) {
		display: grid;
		grid-template-columns: minmax(6rem, 38%) 1fr;
		align-items: stretch;
		height: 100%;
	}

	:scope.compact .image-wrapper {
		aspect-ratio: auto;
		overflow: hidden;
		min-height: 6rem;
	}

	:scope.compact img {
		border-bottom: none;
	}

	:scope.compact .project-info {
		padding: 0.75rem 1rem;
		justify-content: center;
	}

	:scope.compact .project-title {
		font-size: 1.05rem;
	}

	/* nothing to show next to the text, so let it take the full width */
	:scope.compact > :global(.project-card:not(:has(img[src]))) {
		grid-template-columns: 1fr;
	}
`;

export default ProjectCard;
